/**
 * <6_10> DataManagement read-only / view-as-customer rules.
 *
 * Pure helpers, kept in their own module so they can be unit-tested without dragging in
 * the heavy view's transitive deps (axios, react-hook-form, ContactForm, hydration polling, etc.).
 */

/** Admin uses this view to "view as a customer" via the Jobs selector — strictly read-only. */
export const isViewReadOnly = (isAdmin) => Boolean(isAdmin);

/**
 * Whether a request would go out with nothing to bound it, and so must not be sent at all.
 *
 * Deliberately not a question about who is reading — it was, and that was the bug. An unbounded request
 * comes back wrong two different ways, and only one of them is an admin's problem:
 *
 *  - With no cid — an admin who has selected nothing, which `_deriveCID()` turns into 0 — SaaS reads the
 *    absence as every company in `backupsaas_settings`, ArkBridge media never onboarded to the portal
 *    included.
 *  - With a cid but no job id, ItemListFulfiller and ItemCountFulfiller substitute the deployment's own
 *    configured `JobId` rather than reading the absence as "every job". So a customer at 'All Jobs' was
 *    shown one arbitrary job's tapes, consistently and plausibly enough that nothing on screen contradicted
 *    it. That is the case the old `isAdmin` gate let straight through.
 *
 * The escape is a job list either way. 'All Jobs' in the header means the jobs the selector lists, and those
 * ids bound the query whether or not a cid goes with them: JobID is globally unique, so the union across
 * customers is exact, and anded with a cid it narrows to that customer's own jobs. This is the same
 * substitution the dashboard's Media Metrics card makes, so a tile's figure and the rows this view lists
 * cover the same jobs. With ids in hand there is nothing to skip.
 *
 * @param {number[]} [scopeJobIds] - The jobs the request will be bounded by. Omitted, or anything that is
 *        not a list, → none: the caller having no list rather than a list that came back empty.
 */
export const shouldSkipFetch = (scopeJobIds = []) =>
    !(Array.isArray(scopeJobIds) ? scopeJobIds : []).length;
